<template>
  <div
    :style="{
      overflowY: realtime_mode ? 'hidden' : 'auto',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'stretch',
      alignItems: 'stretch',
      height: '100%',
    }">
    <div
      style="width: calc(100% - 50px)"
      class="mb-3 pop-up-tabs-horizontal">
      <v-tabs
        grow
        show-arrows
        v-if="tabs.length > 1 && !disable_tabbing"
        v-model="tab">
        <v-tab v-for="tabname in tabs">{{ (tabLabels[tabname] || tabname).replaceAll('_', ' ') }}</v-tab>
      </v-tabs>
    </div>
    <v-window
      v-model="tab"
      :style="realtime_mode
        ? 'flex-grow: 1; overflow: hidden;'
        : 'flex-grow: 1; overflow: auto;'">
      <div
        v-if="tabsProcessed"
        :style="hasSubGroups && realtime_mode
          ? 'display: flex; overflow-x: auto; height: 100%; gap: 16px; padding-bottom: 8px;'
          : realtime_mode
            ? 'display: flex; flex-direction: column; flex-wrap: wrap; align-content: flex-start; overflow-x: auto; height: 100%; gap: 0 20px; padding-bottom: 8px;'
            : 'display: block;'"
        :class="{ 'scene-data-columns': realtime_mode && !hasSubGroups }">
        <div
          v-for="(groupItems, groupName) in displayGroups"
          :key="groupName"
          :class="{ 'subgroup-panel': hasSubGroups && realtime_mode }"
          :style="!hasSubGroups && realtime_mode ? 'display: contents' : ''">
          <div
            v-if="hasSubGroups && realtime_mode"
            class="subgroup-header">
            {{ subGroupLabel(groupName) }}
          </div>
          <div
            :class="{ 'subgroup-content': hasSubGroups && realtime_mode }"
            :style="!hasSubGroups && realtime_mode ? 'display: contents' : ''">
        <div
          v-for="(item, i) in groupItems"
          :key="(item, item.name)"
          :class="{ 'scene-column-item': !hasSubGroups && realtime_mode }">
          <v-lazy
            v-if="item.mode !== 'R' && item.type"
            :options="{
              threshold: 0,
            }"
            min-height="60px">
            <!--  Exposed Scene Ports -->
            <div
              v-if="item.type == 'template'"
              class="scene-item">
              <SceneExposedScenePort
                v-if="state.beta || nestedVersionCheck(deluge)"
                @valueChanged="emit('valueChanged', $event)"
                :hide_activate_controls="hide_activate_controls"
                :realtime_mode="realtime_mode"
                :item="item"
                :myKey="item.name"
                :current-template="currentTemplate"
                :originLevel="originLevel"
                :resetFunc="resetFunc"
                :safeTemplates="safeTemplates"
                :createOrDeleteSceneData="createOrDeleteSceneData"
                :openSceneOptions="openSceneOptions"
                :hideicons="hideicons"
                :readOnlyMode="readOnlyMode" />
            </div>
            <!-- Scene Ports -->
            <div
              v-else-if="item.type == 'ventuz_scene_port'"
              class="scene-item">
              <ScenePort
                v-if="state.beta || nestedVersionCheck(deluge)"
                @valueChanged="emit('valueChanged', $event)"
                :hide_activate_controls="hide_activate_controls"
                :realtime_mode="realtime_mode"
                :item="item"
                :myKey="item.name"
                :current-template="currentTemplate"
                :originLevel="originLevel"
                :resetFunc="resetFunc"
                :safeTemplates="safeTemplates"
                :createOrDeleteSceneData="createOrDeleteSceneData"
                :openSceneOptions="openSceneOptions"
                :hideicons="hideicons"
                :readOnlyMode="readOnlyMode" />
            </div>
            <!-- Scene Port Layer (Ventuz) -->
            <div
              v-else-if="item.type == 'ventuz_scene_layer'"
              class="scene-item">
              <ScenePortLayer
                v-if="state.beta || nestedVersionCheck(deluge)"
                @valueChanged="emit('valueChanged', $event)"
                :hide_activate_controls="hide_activate_controls"
                :realtime_mode="realtime_mode"
                :item="item"
                :myKey="item.name"
                :current-template="currentTemplate"
                :originLevel="originLevel"
                :resetFunc="resetFunc"
                :safeTemplates="safeTemplates"
                :createOrDeleteSceneData="createOrDeleteSceneData"
                :openSceneOptions="openSceneOptions"
                :hideicons="hideicons"
                :readOnlyMode="readOnlyMode" />
            </div>
            <!-- Scene Port Layer (Orb) -->
            <div
              v-else-if="item.type == 'orb_scene_layer'"
              class="scene-item">
              <ScenePortLayer
                v-if="state.beta || nestedVersionCheck(deluge)"
                @valueChanged="emit('valueChanged', $event)"
                :hide_activate_controls="hide_activate_controls"
                :realtime_mode="realtime_mode"
                :item="item"
                :myKey="item.name"
                :current-template="currentTemplate"
                :originLevel="originLevel"
                :resetFunc="resetFunc"
                :safeTemplates="
                  safeTemplates.filter((t) =>
                    t.extra_info?.template_type?.includes('orb')
                  )
                "
                :createOrDeleteSceneData="createOrDeleteSceneData"
                :openSceneOptions="openSceneOptions"
                :hideicons="hideicons"
                :readOnlyMode="readOnlyMode" />
            </div>
            <!-- Asset -->
            <div
              v-else-if="item.type.startsWith('asset')"
              class="scene-item">
              <SceneAsset
                @valueChanged="emit('valueChanged', $event)"
                :disableFiltering="disableFiltering"
                :hide_activate_controls="hide_activate_controls"
                :item="item"
                :myKey="item.name"
                :multiple="item.type == 'asset_list'"
                :current-template="currentTemplate"
                :originLevel="originLevel"
                :resetFunc="resetFunc"
                :hideicons="hideicons"
                :hide_pin_controls="hide_pin_controls"
                :readOnlyMode="readOnlyMode" />
            </div>
            <!-- Asset -->
            <div
              v-else-if="item.type == 'asset_list'"
              class="scene-item">
              <SceneAsset
                @valueChanged="emit('valueChanged', $event)"
                :disableFiltering="disableFiltering"
                :hide_activate_controls="hide_activate_controls"
                :item="item"
                :myKey="item.name"
                :multiple="true"
                :current-template="currentTemplate"
                :originLevel="originLevel"
                :resetFunc="resetFunc"
                :hideicons="hideicons"
                :hide_pin_controls="hide_pin_controls"
                :readOnlyMode="readOnlyMode" />
            </div>
            <!-- Color -->
            <div
              v-else-if="item.type == 'color'"
              class="scene-item">
              <SceneColor
                @valueChanged="emit('valueChanged', $event)"
                :hide_activate_controls="hide_activate_controls"
                :item="item"
                :myKey="item.name"
                :current-template="currentTemplate"
                :originLevel="originLevel"
                :resetFunc="resetFunc"
                :hideicons="hideicons"
                :hide_pin_controls="hide_pin_controls"
                :readOnlyMode="readOnlyMode"
                :realtime_mode="realtime_mode" />
            </div>
            <!-- Slider -->
            <div
              v-else-if="item.type == 'slider'"
              class="scene-item">
              <SceneSlider
                @valueChanged="emit('valueChanged', $event)"
                :hide_activate_controls="hide_activate_controls"
                :item="item"
                :myKey="item.name"
                :current-template="currentTemplate"
                :originLevel="originLevel"
                :resetFunc="resetFunc"
                :hideicons="hideicons"
                :hide_pin_controls="hide_pin_controls"
                :readOnlyMode="readOnlyMode" />
            </div>
            <!-- Bool -->
            <div
              v-else-if="item.type == 'bool'"
              class="scene-item">
              <SceneBool
                @valueChanged="emit('valueChanged', $event)"
                :hide_activate_controls="hide_activate_controls"
                :item="item"
                :myKey="item.name"
                :current-template="currentTemplate"
                :originLevel="originLevel"
                :resetFunc="resetFunc"
                :hideicons="hideicons"
                :hide_pin_controls="hide_pin_controls"
                :readOnlyMode="readOnlyMode" />
            </div>
            <!-- Trigger-->
            <div
              v-else-if="item.type == 'trigger'"
              class="scene-item">
              <SceneTrigger
                @valueChanged="emit('valueChanged', $event)"
                :hide_activate_controls="hide_activate_controls"
                :item="item"
                :myKey="item.name"
                :realtime_mode="realtime_mode"
                :current-template="currentTemplate"
                :originLevel="originLevel"
                :resetFunc="resetFunc"
                :hideicons="hideicons"
                :hide_pin_controls="hide_pin_controls"
                :readOnlyMode="readOnlyMode" />
            </div>
            <!-- Int or Float -->
            <div
              v-else-if="item.type == 'int' || item.type == 'float'"
              class="scene-item">
              <SceneIntFloat
                @valueChanged="emit('valueChanged', $event)"
                :hide_activate_controls="hide_activate_controls"
                :item="item"
                :myKey="item.name"
                :current-template="currentTemplate"
                :originLevel="originLevel"
                :resetFunc="resetFunc"
                :hideicons="hideicons"
                :hide_pin_controls="hide_pin_controls"
                :readOnlyMode="readOnlyMode" />
            </div>
            <!-- String -->
            <div
              v-else-if="item.type == 'json'"
              class="scene-item">
              <SceneString
                @valueChanged="emit('valueChanged', $event)"
                :hide_activate_controls="hide_activate_controls"
                :item="item"
                :myKey="item.name"
                :current-template="currentTemplate"
                :originLevel="originLevel"
                :resetFunc="resetFunc"
                :hideicons="hideicons"
                :hide_pin_controls="hide_pin_controls"
                :readOnlyMode="readOnlyMode" />
            </div>
            <!-- Enum -->
            <div
              v-else-if="item.type == 'enum'"
              class="scene-item">
              <SceneEnum
                @valueChanged="emit('valueChanged', $event)"
                :hide_activate_controls="hide_activate_controls"
                :item="item"
                :myKey="item.name"
                :current-template="currentTemplate"
                :originLevel="originLevel"
                :resetFunc="resetFunc"
                :hideicons="hideicons"
                :hide_pin_controls="hide_pin_controls"
                :readOnlyMode="readOnlyMode" />
            </div>
            <!-- Unknown? -->
            <div
              v-else
              class="scene-item">
              <SceneUnknown
                @valueChanged="emit('valueChanged', $event)"
                :hide_activate_controls="hide_activate_controls"
                :item="item"
                :myKey="item.name"
                :current-template="currentTemplate"
                :originLevel="originLevel"
                :resetFunc="resetFunc"
                :hideicons="hideicons"
                :hide_pin_controls="hide_pin_controls"
                :readOnlyMode="readOnlyMode" />
            </div>
          </v-lazy>
        </div>
          </div>
        </div>
      </div>
    </v-window>
  </div>
</template>
<script setup>
// SceneDataItems
import SceneAsset from './SceneDataSubItems/SceneAsset.vue';
import SceneBool from './SceneDataSubItems/SceneBool.vue';
import SceneColor from './SceneDataSubItems/SceneColor.vue';
import SceneEnum from './SceneDataSubItems/SceneEnum.vue';
import SceneExposedScenePort from './SceneDataSubItems/SceneExposedScenePort.vue';
import SceneIntFloat from './SceneDataSubItems/SceneIntFloat.vue';
import ScenePort from './SceneDataSubItems/ScenePort.vue';
import ScenePortLayer from './SceneDataSubItems/ScenePortLayer.vue';
import SceneSlider from './SceneDataSubItems/SceneSlider.vue';
import SceneString from './SceneDataSubItems/JSONEditor.vue';
import SceneTrigger from './SceneDataSubItems/SceneTrigger.vue';
import SceneUnknown from './SceneDataSubItems/SceneUnknown.vue';
import { deluge, state } from '@/stores/deluge.js';
import { onMounted, ref, watch, onBeforeMount, reactive, computed } from 'vue';
import { nestedVersionCheck } from './utils';

const tab = ref(0);
const tabs = ref([]);
const tabLabels = ref({});
const tabsProcessed = ref(false);
const props = defineProps([
  'currentObj',
  'currentTemplate',
  'searchCriteria',
  'searchResults',
  'resetFunc',
  'originLevel',
  'originObj',
  'hideicons',
  'createOrDeleteSceneData',
  'openSceneOptions',
  'safeTemplates',
  'realtime_mode',
  'disable_tabbing',
  'hide_pin_controls',
  'hide_activate_controls',
  'breadcrumbs',
  'readOnlyMode',
  'disableFiltering',
]);
watch(
  () => props.currentObj,
  () => {
    tabs.value = processTabs();
    console.log(tabs.value);
  }
);

watch(tab, () => {
  // if tab changes, set tab value to breadcrumb
  Object.values(props.breadcrumbs)[props.breadcrumbs.length - 1].tab =
    tab.value;
  // assign tabname as well
  Object.values(props.breadcrumbs)[props.breadcrumbs.length - 1].tabname =
    tabs.value[tab.value];
});

const emit = defineEmits(['valueChanged']);

onBeforeMount(() => {
  tabs.value = processTabs();
});
function moveAssetAndTemplatesToTop(filteredObj) {
  // templates first, then assets, then the rest
  let sceneLayers = [];
  let other = [];
  const sceneLayerTypes = ['ventuz_scene_layer', 'orb_scene_layer'];
  for (const key of Object.keys(filteredObj)) {
    if (sceneLayerTypes.includes(filteredObj[key].type)) {
      sceneLayers.push(filteredObj[key]);
    } else {
      other.push(filteredObj[key]);
    }
  }
  // sort alphabetical
  sceneLayers.sort((a, b) => a.name.localeCompare(b.name));
  return sceneLayers.concat(other);
}
function filterByTabName(tabName) {
  let filteredObj = {};
  // returns filtered props.currentObj object by tabName (if tabName in keys)
  if (!props.currentObj) return filteredObj;
  if (Object.keys(props.currentObj).length === 0) return filteredObj;

  if (props.disable_tabbing) {
    for (const key of Object.keys(props.currentObj)) {
      if (props.currentObj[key]?.transform) {
        continue;
      }
      if (props.currentObj[key]?.label === 'SceneToggle') {
        continue;
      }
      filteredObj[key] = props.currentObj[key];
    }

    return filteredObj;
  } else {
    if (
      tabs.value.length == 0 ||
      (tabs.value.length == 1 && tabs.value?.[0] == 'Other')
    ) {
      for (const key of Object.keys(props.currentObj)) {
        if (
          !props.searchCriteria
            ? true
            : !!props.searchResults[props.currentObj[key].name]
        ) {
          if (props.currentObj[key]?.transform) {
            continue;
          }
          if (props.currentObj[key]?.label === 'SceneToggle') {
            continue;
          }
          filteredObj[key] = props.currentObj[key];
        }
      }
      return filteredObj;
    } else {
      for (const key of Object.keys(props.currentObj)) {
        if (props.currentObj[key]?.transform) {
          continue;
        }
        if (props.currentObj[key]?.label === 'SceneToggle') {
          continue;
        }
        if (key.split('.')[1] === tabName) {
          filteredObj[key] = props.currentObj[key];
        } else if (tabName == 'Other' && !(key.split('.').length - 1 > 1)) {
          filteredObj[key] = props.currentObj[key];
        }
      }
      return filteredObj;
    }
  }
}
let tabTracker = {};
let lastbreadcrumbstr = '';
function processTabs() {
  // add categories to tabs if key has two '.' (e.g if props.currentObj is .Playlist.Background, add .Playlist to tabs)
  if (props.currentObj) {
    const tabs = [];

    const labels = {};
    for (const key of Object.keys(props.currentObj)) {
      if (key.split('.').length - 1 > 1) {
        const tabName = key.split('.')[1];
        if (!tabs.includes(tabName)) {
          tabs.push(tabName);
        }
        if (!labels[tabName] && props.currentObj[key]?.group_label) {
          labels[tabName] = props.currentObj[key].group_label;
        }
      } else {
        if (!tabs.includes('Other')) {
          tabs.push('Other');
        }
      }
    }
    tabLabels.value = labels;
    // ensure other is last

    if (tabs.includes('Other')) {
      const index = tabs.indexOf('Other');
      tabs.splice(index, 1);
      tabs.push('Other');
    }
    let breadcrumbstr = '';
    for (const [key, breadcrumb] of Object.entries(props.breadcrumbs)) {
      breadcrumbstr +=
        breadcrumb.name + breadcrumb.uid + key + breadcrumb.portLabel;
    }
    if (tabTracker[breadcrumbstr] === undefined) {
      tabTracker[breadcrumbstr] = 0;
    }
    if (lastbreadcrumbstr !== breadcrumbstr) {
      Object.values(props.breadcrumbs)[props.breadcrumbs.length - 1].tab =
        Object.values(props.breadcrumbs)[props.breadcrumbs.length - 1].tab || 0;
    } else {
      tabTracker[breadcrumbstr] = tab.value;
    }
    lastbreadcrumbstr = breadcrumbstr;
    tabsProcessed.value = true;
    return tabs;
  }
  tabsProcessed.value = true;
  return [];
}

const hasSubGroups = computed(() => {
  const filtered = filterByTabName(tabs.value[tab.value]);
  for (const key of Object.keys(filtered)) {
    // 3-level keys like .Tab.SubGroup.Key have 4 parts when split by '.'
    if (key.split('.').length > 3) {
      return true;
    }
  }
  return false;
});

const displayGroups = computed(() => {
  const filtered = filterByTabName(tabs.value[tab.value]);
  const sorted = moveAssetAndTemplatesToTop(filtered);

  if (!hasSubGroups.value || !props.realtime_mode) {
    return { '_all': sorted };
  }

  // Group by sub-group name (3rd segment of the key)
  const groups = {};
  for (const item of sorted) {
    const parts = item.name.split('.');
    // parts: ['', 'TabName', 'SubGroupName', 'KeyName']
    const subGroup = parts.length > 3 ? parts[2] : '_other';
    if (!groups[subGroup]) {
      groups[subGroup] = [];
    }
    groups[subGroup].push(item);
  }
  return groups;
});

function subGroupLabel(groupName) {
  if (groupName === '_all' || groupName === '_other') return '';
  // Insert space before uppercase letters and replace underscores
  return groupName.replace(/([a-z])([A-Z])/g, '$1 $2').replaceAll('_', ' ');
}
</script>
<style>
.scene-item {
  display: flex;
  overflow: visible;
}

.scene-column-item {
  width: 500px;
  flex-shrink: 0;
}

.scene-item > .v-card {
  min-height: 102px;
}

.scene-item .asset-thumb {
  margin-left: 8px;
}

.pop-up-tabs-horizontal .v-slide-group__container {
  width: 200px !important;
}

/* Force v-window internals to propagate height for flex column wrap layout */
.v-window__container {
  height: 100%;
}

.subgroup-panel {
  min-width: 420px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  overflow: hidden;
}

.subgroup-header {
  padding: 8px 12px;
  font-weight: 500;
  font-size: 14px;
  opacity: 0.8;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}

.subgroup-content {
  flex-grow: 1;
  overflow-y: auto;
  padding: 8px;
}
</style>
